import React from 'react';

import { Button } from '../base/Button';
import { borderRadius, colors, spacing, typography } from '../../styles/tokens';

type BuylistCustomerFeedbackPanelProps = {
  /** Offer-email body. Same string the server sends. */
  text: string;
  onCopied?: (message: string) => void;
};

/**
 * Staff-only copy of the customer offer summary.
 * Copy does not send mail — Send offer is a separate workflow action.
 */
export function BuylistCustomerFeedbackPanel({
  text,
  onCopied,
}: BuylistCustomerFeedbackPanelProps) {
  const copy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      onCopied?.('Feedback copied.');
    } catch {
      onCopied?.('Select the feedback text and copy it manually.');
    }
  };

  return (
    <section style={styles.card} aria-label="Customer feedback">
      <h2 style={{ ...typography.h2, marginTop: 0 }}>Customer feedback</h2>
      <p style={styles.hint}>
        Final offer, value received, and the receive change list — the same summary as the offer
        email. Copy it here for the customer.
      </p>
      <textarea
        readOnly
        value={text}
        aria-label="Customer feedback summary"
        style={styles.textarea}
        spellCheck={false}
      />
      <div style={styles.actions}>
        <Button type="button" variant="outline" onClick={() => void copy()} disabled={!text}>
          Copy feedback
        </Button>
      </div>
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    background: colors.surface,
    marginBottom: spacing.lg,
  },
  hint: {
    ...typography.small,
    color: colors.textSecondary,
    margin: `0 0 ${spacing.sm}`,
  },
  textarea: {
    width: '100%',
    minHeight: 220,
    boxSizing: 'border-box',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    ...typography.small,
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    background: colors.gray50,
    color: colors.text,
    resize: 'vertical',
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
};
